import React, { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { DataTable } from '@/components/ui/DataTable'

interface NavItem {
  id: string
  label: string
  app: string
  path: string
  requiredOUs: string
  visible: boolean
}

const NavigationSettings: React.FC = () => {
  const [navItems, setNavItems] = useState<NavItem[]>([
    { id: '1', label: 'Users', app: 'Core', path: '/users', requiredOUs: 'All', visible: true },
    { id: '2', label: 'Companies', app: 'Core', path: '/companies', requiredOUs: 'All', visible: true },
    { id: '3', label: 'Projects', app: 'Core', path: '/projects', requiredOUs: 'Engineering', visible: true },
    { id: '4', label: 'Settings', app: 'Core', path: '/settings', requiredOUs: 'Admin only', visible: true },
  ])
  const [registerModalOpen, setRegisterModalOpen] = useState(false)
  const [newNavItem, setNewNavItem] = useState({
    label: '',
    app: '',
    path: '',
    icon: '',
    parentItem: '',
    requiredOUs: '',
    sortOrder: '0',
  })

  const handleAddNavItem = () => {
    if (newNavItem.label && newNavItem.app && newNavItem.path) {
      const navItem: NavItem = {
        id: String(navItems.length + 1),
        label: newNavItem.label,
        app: newNavItem.app,
        path: newNavItem.path,
        requiredOUs: newNavItem.requiredOUs || 'All',
        visible: true,
      }
      setNavItems([...navItems, navItem])
      setNewNavItem({ label: '', app: '', path: '', icon: '', parentItem: '', requiredOUs: '', sortOrder: '0' })
      setRegisterModalOpen(false)
    }
  }

  const handleToggleVisible = (id: string) => {
    setNavItems(navItems.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item)))
  }

  return (
    <div>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 24px 0', color: 'var(--color-black)' }}>
        Navigation settings
      </h2>

      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => setRegisterModalOpen(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          + Register nav item
        </button>
      </div>

      <DataTable
        columns={[
          { key: 'label', header: 'Label', render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div> },
          { key: 'app', header: 'App', render: (value: string) => value },
          { key: 'path', header: 'Path', render: (value: string) => <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>{value}</code> },
          { key: 'requiredOUs', header: 'Required OUs', render: (value: string) => value },
          {
            key: 'visible',
            header: 'Visible',
            render: (value: boolean) => (
              <input
                type="checkbox"
                checked={value}
                onChange={() => {}}
                style={{ cursor: 'pointer' }}
              />
            ),
          },
        ]}
        data={navItems}
        rowKey="path"
        actions={[
          { label: 'Edit', onClick: (_row: any) => {} },
          { label: 'Delete', onClick: (_row: any) => {}, variant: 'danger' as const },
        ]}
      />

      {/* Register modal */}
      <Modal
          isOpen={registerModalOpen}
          title="Register navigation item"
          onClose={() => setRegisterModalOpen(false)}
          isDirty={newNavItem.label !== '' || newNavItem.app !== '' || newNavItem.path !== '' || newNavItem.icon !== '' || newNavItem.requiredOUs !== ''}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRegisterModalOpen(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--color-white)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddNavItem}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Register
              </button>
            </div>
          }
        >
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Label (required)
              </label>
              <input
                type="text"
                value={newNavItem.label}
                onChange={(e) => setNewNavItem({ ...newNavItem, label: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                App name (required)
              </label>
              <input
                type="text"
                value={newNavItem.app}
                onChange={(e) => setNewNavItem({ ...newNavItem, app: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Path (required)
              </label>
              <input
                type="text"
                value={newNavItem.path}
                onChange={(e) => setNewNavItem({ ...newNavItem, path: e.target.value })}
                placeholder="/path"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Icon name
              </label>
              <input
                type="text"
                value={newNavItem.icon}
                onChange={(e) => setNewNavItem({ ...newNavItem, icon: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Parent item
              </label>
              <select
                value={newNavItem.parentItem}
                onChange={(e) => setNewNavItem({ ...newNavItem, parentItem: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'var(--color-white)',
                  cursor: 'pointer',
                }}
              >
                <option value="">None (top level)</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Required OUs
              </label>
              <input
                type="text"
                value={newNavItem.requiredOUs}
                onChange={(e) => setNewNavItem({ ...newNavItem, requiredOUs: e.target.value })}
                placeholder="All"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Sort order
              </label>
              <input
                type="number"
                value={newNavItem.sortOrder}
                onChange={(e) => setNewNavItem({ ...newNavItem, sortOrder: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
          </div>
        </Modal>
    </div>
  )
}

export default NavigationSettings
